"use client"

import { useState, useRef, useCallback } from "react"
import type { PCBStore } from "@/hooks/use-pcb-store"
import type { Layer } from "@/lib/pcb-types"
import { LAYER_COLORS } from "@/lib/pcb-types"
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Layers,
  MoreHorizontal,
  Ban,
  Network,
  Pencil,
  GripHorizontal,
} from "lucide-react"
import { Slider } from "@/components/ui/slider"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"

interface LayersPanelProps {
  store: PCBStore
  selectedNet: string | null
  onSelectNet: (net: string | null) => void
  onNetDoubleClick: (net: string) => void
}

export function LayersPanel({ store, selectedNet, onSelectNet, onNetDoubleClick }: LayersPanelProps) {
  const [expandedLayer, setExpandedLayer] = useState<string | null>(null)
  const [editingName, setEditingName] = useState<string | null>(null)
  const [nameValue, setNameValue] = useState("")
  const [dragLayerIndex, setDragLayerIndex] = useState<number | null>(null)
  const [dragOverLayerIndex, setDragOverLayerIndex] = useState<number | null>(null)
  const [dragObjectInfo, setDragObjectInfo] = useState<{ layerId: string; objectId: string; index: number } | null>(null)
  
  // Resizable divider state (percentage for layers section, default 66%)
  const [layersHeightPercent, setLayersHeightPercent] = useState(66)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDraggingDivider = useRef(false)
  
  // Handle divider drag
  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isDraggingDivider.current = true
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingDivider.current || !containerRef.current) return
      const containerRect = containerRef.current.getBoundingClientRect()
      const newPercent = ((e.clientY - containerRect.top) / containerRect.height) * 100
      // Clamp between 20% and 80%
      setLayersHeightPercent(Math.max(20, Math.min(80, newPercent)))
    }
    
    const handleMouseUp = () => {
      isDraggingDivider.current = false
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
    
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }, [])
  
  // Net rename state
  const [editingNetName, setEditingNetName] = useState<string | null>(null)
  const [netNameValue, setNetNameValue] = useState("")
  
  // Rename net across all objects
  const handleRenameNet = (oldName: string, newName: string) => {
    if (!newName.trim() || newName === oldName) {
      setEditingNetName(null)
      return
    }
    // Update all objects with this net name
    store.layers.forEach(layer => {
      layer.objects.forEach(obj => {
        if (obj.net === oldName) {
          store.updateObject(layer.id, obj.id, { net: newName.trim() })
        }
      })
    })
    // Update selected net if it was the renamed one
    if (selectedNet === oldName) {
      onSelectNet(newName.trim())
    }
    setEditingNetName(null)
  }
  const [dragOverObjectIndex, setDragOverObjectIndex] = useState<number | null>(null)
  const [dragOverTargetLayerId, setDragOverTargetLayerId] = useState<string | null>(null)

  const handleStartRename = (layer: Layer) => {
    setEditingName(layer.id)
    setNameValue(layer.name)
  }

  const handleFinishRename = (layerId: string) => {
    if (nameValue.trim()) {
      store.renameLayer(layerId, nameValue.trim())
    }
    setEditingName(null)
  }

  // Compute all unique nets from objects
  const allNets = Array.from(new Set(
    store.layers.flatMap(l => l.objects.map(o => o.net).filter((n): n is string => Boolean(n)))
  )).sort()

  return (
    <div ref={containerRef} className="flex h-full flex-col border-l border-border bg-sidebar text-sidebar-foreground overflow-hidden">
      {/* Layers Section - resizable height */}
      <div className="flex flex-col overflow-hidden" style={{ height: `${layersHeightPercent}%` }}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5 shrink-0">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold tracking-wide uppercase">Layers</span>
          </div>
          <button
            onClick={() => store.addLayer()}
            className="flex items-center gap-1 rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
            title="Add layer"
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
        </div>

        {/* Layer list with scrolling */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-1.5">
          {store.layers.map((layer, index) => {
            const isActive = store.activeLayerId === layer.id
            const isExpanded = expandedLayer === layer.id
            const objectCount = layer.objects.length

            return (
              <div key={layer.id} className="mb-0.5">
                {/* Layer row */}
                <div
                  draggable
                  onDragStart={() => setDragLayerIndex(index)}
                  onDragEnd={() => { setDragLayerIndex(null); setDragOverLayerIndex(null); setDragOverTargetLayerId(null) }}
                  onDragOver={(e) => { 
                    e.preventDefault()
                    if (dragLayerIndex !== null) setDragOverLayerIndex(index)
                    if (dragObjectInfo !== null) setDragOverTargetLayerId(layer.id)
                  }}
                  onDragLeave={() => { if (dragObjectInfo !== null) setDragOverTargetLayerId(null) }}
                  onDrop={() => {
                    if (dragLayerIndex !== null && dragLayerIndex !== index) {
                      store.reorderLayers(dragLayerIndex, index)
                    }
                    // Handle object drop from another layer
                    if (dragObjectInfo !== null && dragObjectInfo.layerId !== layer.id) {
                      store.moveObjectToLayer(dragObjectInfo.layerId, dragObjectInfo.objectId, layer.id)
                    }
                    setDragLayerIndex(null)
                    setDragOverLayerIndex(null)
                    setDragObjectInfo(null)
                    setDragOverTargetLayerId(null)
                  }}
                  className={`group flex items-center gap-1 rounded px-1.5 py-1.5 transition-colors cursor-pointer ${
                    isActive
                      ? "bg-primary/10 ring-1 ring-primary/30"
                      : "hover:bg-secondary"
                  } ${dragOverLayerIndex === index && dragLayerIndex !== null ? "ring-2 ring-primary" : ""} ${dragOverTargetLayerId === layer.id && dragObjectInfo !== null && dragObjectInfo.layerId !== layer.id ? "ring-2 ring-green-500 bg-green-500/10" : ""}`}
                  onClick={() => store.setActiveLayer(layer.id)}
                >
                  {/* Drag handle */}
                  <GripVertical className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-60 shrink-0 cursor-grab" />

                  {/* Expand toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setExpandedLayer(isExpanded ? null : layer.id)
                    }}
                    className="shrink-0"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                    )}
                  </button>

                  {/* Color indicator */}
                  <div
                    className="h-3 w-3 rounded-sm shrink-0 ring-1 ring-border"
                    style={{ backgroundColor: layer.color }}
                  />

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    {editingName === layer.id ? (
                      <input
                        type="text"
                        value={nameValue}
                        onChange={(e) => setNameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleFinishRename(layer.id)
                          if (e.key === "Escape") setEditingName(null)
                        }}
                        onBlur={() => handleFinishRename(layer.id)}
                        className="w-full bg-transparent text-xs font-mono text-foreground outline-none border-b border-primary"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span
                        className={`text-xs font-mono truncate block select-none ${
                          !layer.visible ? "text-muted-foreground line-through" : ""
                        }`}
                        onDoubleClick={(e) => {
                          e.stopPropagation()
                          handleStartRename(layer)
                        }}
                      >
                        {layer.name}
                      </span>
                    )}
                  </div>

                  {/* Object count */}
                  {objectCount > 0 && (
                    <span className="text-[10px] text-muted-foreground font-mono shrink-0">
                      {objectCount}
                    </span>
                  )}

                  {/* Quick actions */}
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        store.toggleLayerVisibility(layer.id)
                      }}
                      className="rounded p-0.5 transition-colors hover:bg-tool-hover"
                      title={layer.visible ? "Hide layer" : "Show layer"}
                    >
                      {layer.visible ? (
                        <Eye className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <EyeOff className="h-3 w-3 text-muted-foreground/50" />
                      )}
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        store.toggleLayerLock(layer.id)
                      }}
                      className="rounded p-0.5 transition-colors hover:bg-tool-hover"
                      title={layer.locked ? "Unlock layer" : "Lock layer"}
                    >
                      {layer.locked ? (
                        <Lock className="h-3 w-3 text-destructive" />
                      ) : (
                        <Unlock className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
                      )}
                    </button>

                    <DropdownMenu>
                      <DropdownMenuTrigger
                        onClick={(e) => e.stopPropagation()}
                        className="rounded p-0.5 transition-colors hover:bg-tool-hover opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal className="h-3 w-3 text-muted-foreground" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40 bg-popover">
                        <DropdownMenuItem onClick={() => handleStartRename(layer)}>
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => store.clearLayer(layer.id)}>
                          <Ban className="mr-2 h-3 w-3" />
                          Clear objects
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => store.removeLayer(layer.id)}
                          disabled={store.layers.length <= 1}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-3 w-3" />
                          Delete layer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="ml-6 mt-1 mb-2 space-y-2 rounded bg-secondary/50 p-2">
                    {/* Color picker */}
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Color
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {LAYER_COLORS.map((color) => (
                          <button
                            key={color}
                            onClick={() => store.setLayerColorAndUpdateObjects(layer.id, color)}
                            className={`h-5 w-5 rounded-sm ring-1 transition-all ${
                              layer.color === color
                                ? "ring-foreground scale-110"
                                : "ring-border hover:ring-muted-foreground"
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Opacity */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Opacity
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          {(layer.opacity * 100).toFixed(0)}%
                        </span>
                      </div>
                      <Slider
                        min={0}
                        max={100}
                        step={5}
                        value={[layer.opacity * 100]}
                        onValueChange={([value]) => store.setLayerOpacity(layer.id, value / 100)}
                        className="w-full"
                      />
                    </div>

                    {/* Object list */}
                    {layer.objects.length > 0 && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Objects ({layer.objects.length})
                        </span>
                        <div className="max-h-32 overflow-y-auto space-y-0.5 custom-scrollbar">
                          {layer.objects.map((obj, objIndex) => (
                            <div
                              key={obj.id}
                              draggable
                              onDragStart={(e) => { e.stopPropagation(); setDragObjectInfo({ layerId: layer.id, objectId: obj.id, index: objIndex }) }}
                              onDragEnd={() => { setDragObjectInfo(null); setDragOverObjectIndex(null); setDragOverTargetLayerId(null) }}
                              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverObjectIndex(objIndex) }}
                              onDrop={(e) => {
                                e.stopPropagation()
                                if (dragObjectInfo && dragObjectInfo.layerId === layer.id && dragObjectInfo.index !== objIndex) {
                                  store.reorderObjectsInLayer(layer.id, dragObjectInfo.index, objIndex)
                                }
                                setDragObjectInfo(null)
                                setDragOverObjectIndex(null)
                                setDragOverTargetLayerId(null)
                              }}
                              className={`flex items-center justify-between rounded bg-background/50 px-2 py-1 text-[10px] font-mono cursor-pointer hover:bg-background/80 transition-colors overflow-hidden max-w-full ${obj.visible === false ? "opacity-50" : ""} ${dragOverObjectIndex === objIndex && dragObjectInfo?.layerId === layer.id ? "ring-1 ring-primary" : ""}`}
                              onDoubleClick={() => {
                                // Center and fit the object on canvas
                                const container = document.getElementById("pcb-canvas-container")
                                if (!container || obj.points.length === 0) return
                                let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
                                // For circles, calculate full bounds using radius
                                if (obj.type === "circle" && obj.points.length >= 2) {
                                  const center = obj.points[0]
                                  const edge = obj.points[1]
                                  const radius = Math.hypot(edge.x - center.x, edge.y - center.y)
                                  minX = center.x - radius
                                  minY = center.y - radius
                                  maxX = center.x + radius
                                  maxY = center.y + radius
                                } else {
                                  for (const p of obj.points) {
                                    if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y
                                    if (p.x > maxX) maxX = p.x; if (p.y > maxY) maxY = p.y
                                  }
                                }
                                const objW = Math.max(maxX - minX, 50)
                                const objH = Math.max(maxY - minY, 50)
                                const cw = container.clientWidth, ch = container.clientHeight
                                const padding = 80
                                const scaleX = (cw - padding * 2) / objW
                                const scaleY = (ch - padding * 2) / objH
                                const newZoom = Math.max(0.5, Math.min(10, Math.min(scaleX, scaleY)))
                                const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2
                                store.setZoom(newZoom)
                                store.setPanOffset({ x: cw / 2 - cx * newZoom, y: ch / 2 - cy * newZoom })
                              }}
                              title="Double-click to center on canvas"
                            >
                              <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden w-0">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    store.updateObject(layer.id, obj.id, { visible: obj.visible === false ? true : false })
                                  }}
                                  className="shrink-0 p-0.5 hover:bg-tool-hover rounded transition-colors"
                                  title={obj.visible === false ? "Show object" : "Hide object"}
                                >
                                  {obj.visible === false ? (
                                    <EyeOff className="h-2.5 w-2.5 text-muted-foreground/50" />
                                  ) : (
                                    <Eye className="h-2.5 w-2.5 text-muted-foreground" />
                                  )}
                                </button>
                                {/* Object type icon */}
                                <svg className="h-3 w-3 shrink-0" viewBox="0 0 16 16" fill="none" stroke={obj.color} strokeWidth="1.5">
                                  {obj.type === "polyline" && <polyline points="2,12 6,4 10,10 14,2" />}
                                  {obj.type === "polygon" && <polygon points="8,2 14,6 12,14 4,14 2,6" fill={obj.color} fillOpacity="0.3" />}
                                  {obj.type === "rect" && <rect x="2" y="3" width="12" height="10" fill={obj.color} fillOpacity="0.3" />}
                                  {obj.type === "circle" && <circle cx="8" cy="8" r="6" fill={obj.color} fillOpacity="0.3" />}
                                  {obj.type === "freehand" && <path d="M2,10 Q4,4 8,8 T14,6" />}
                                  {obj.type === "text" && <text x="3" y="12" fontSize="10" fill={obj.color} stroke="none">T</text>}
                                  {obj.type === "marker" && <circle cx="8" cy="8" r="4" fill={obj.color} />}
                                  {obj.type === "probe" && <path d="M3,13 L6,8 M5,9 L10,4" fill={obj.color} fillOpacity="0.3" />}
                                  {obj.type === "measure" && <line x1="2" y1="8" x2="14" y2="8" strokeDasharray="2,2" />}
                                </svg>
                                {/* Comment/text label */}
                                <span className={`truncate text-[9px] ${obj.visible === false ? "text-muted-foreground/50" : "text-muted-foreground"}`} title={obj.comment || obj.text || obj.type}>
                                  {obj.comment || obj.text || obj.type}
                                </span>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  store.removeObject(layer.id, obj.id)
                                }}
                                className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                              >
                                <Trash2 className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          </div>
        </ScrollArea>

        {/* Layers footer info */}
        <div className="border-t border-border px-3 py-1.5 shrink-0">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground font-mono">
            <span>{store.layers.length} layers</span>
            <span>
              {store.layers.reduce((acc, l) => acc + l.objects.length, 0)} objects
            </span>
          </div>
        </div>
      </div>

      {/* Resizable Divider */}
      <div
        onMouseDown={handleDividerMouseDown}
        className="h-2 shrink-0 cursor-ns-resize bg-border hover:bg-primary/30 transition-colors flex items-center justify-center group"
        title="Drag to resize"
      >
        <GripHorizontal className="h-3 w-3 text-muted-foreground/50 group-hover:text-primary/70" />
      </div>

      {/* Netlist Section - fills remaining space at bottom */}
      <div className="flex flex-col flex-1 overflow-hidden min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-3 py-2 shrink-0">
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold tracking-wide uppercase">Netlist</span>
          </div>
          <span className="text-[10px] text-muted-foreground font-mono">{allNets.length} nets</span>
        </div>

        {/* Net list with proper scrolling */}
        <ScrollArea className="flex-1 min-h-0 h-full">
          <div className="p-1.5">
            {allNets.length === 0 ? (
              <div className="text-center py-4 text-[10px] text-muted-foreground">
                No nets defined yet.
                <br />
                <span className="text-[9px]">Double-click objects to assign nets.</span>
              </div>
            ) : (
              allNets.map((net) => {
                const objectCount = store.layers.reduce((acc, l) => 
                  acc + l.objects.filter(o => o.net === net).length, 0
                )
                const isSelected = selectedNet === net
                const isEditing = editingNetName === net
                
                return (
                  <div
                    key={net}
                    className={`flex items-center justify-between rounded px-1.5 py-1 transition-colors group ${
                      isSelected
                        ? "bg-primary/20 ring-1 ring-primary/50 text-foreground"
                        : "hover:bg-secondary text-muted-foreground"
                    }`}
                  >
                    {isEditing ? (
                      <input
                        type="text"
                        value={netNameValue}
                        onChange={(e) => setNetNameValue(e.target.value)}
                        onBlur={() => handleRenameNet(net, netNameValue)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRenameNet(net, netNameValue)
                          if (e.key === "Escape") setEditingNetName(null)
                        }}
                        className="flex-1 bg-secondary border border-border rounded px-1 py-0.5 text-xs font-mono text-foreground outline-none focus:border-primary"
                        autoFocus
                      />
                    ) : (
                      <button
                        onClick={() => onSelectNet(isSelected ? null : net)}
                        onDoubleClick={() => onNetDoubleClick(net)}
                        className="flex-1 text-left truncate"
                        title="Click to highlight, Double-click to blink"
                      >
                        <span className="text-xs font-mono truncate">{net}</span>
                      </button>
                    )}
                    
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <span className="text-[10px] font-mono text-muted-foreground">
                        {objectCount}
                      </span>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-secondary transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-3 w-3" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[120px]">
                          <DropdownMenuItem
                            onClick={() => {
                              setEditingNetName(net)
                              setNetNameValue(net)
                            }}
                          >
                            <Pencil className="h-3 w-3 mr-2" />
                            Rename
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  )
}
